import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Heart, Eye, Loader2, Flag, User, CalendarDays, Sprout, Languages, TreePine, AlertTriangle } from 'lucide-react';
import { api, publicFileUrl } from '../lib/api';
import { toast } from 'sonner';
import ShareButton from '../components/ShareButton';
import { useI18n } from '../i18n';
import LivingImage from '../components/living/LivingImage';
import FindPhoto from '../components/FindPhoto';
import { findPhotoUrl } from '../lib/wildFinds';

const LOCALE_FLAG = {
    en: '🇬🇧', da: '🇩🇰', de: '🇩🇪', sv: '🇸🇪', no: '🇳🇴', nl: '🇳🇱',
    fr: '🇫🇷', es: '🇪🇸', it: '🇮🇹', pt: '🇵🇹', pl: '🇵🇱',
};
const flagFor = (loc) => LOCALE_FLAG[(loc || 'en').slice(0, 2)] || '🌍';

const plantLegend = (cells) => {
    const map = new Map();
    (cells || []).forEach((c) => {
        const key = c.label || c.plant_id || c.emoji;
        if (!key) return;
        const cur = map.get(key) || { emoji: c.emoji, label: c.label || c.plant_id, count: 0 };
        cur.count += 1;
        map.set(key, cur);
    });
    return [...map.values()].sort((a, b) => b.count - a.count);
};

const fmtWhen = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.length === 10 ? iso : (iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z'));
    if (isNaN(d)) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

function BedPreview({ rows, cols, cells }) {
    const grid = [];
    const cellMap = {};
    (cells || []).forEach(c => { cellMap[`${c.r}-${c.c}`] = c; });
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const cell = cellMap[`${r}-${c}`];
            grid.push(
                <div
                    key={`${r}-${c}`}
                    className="aspect-square border border-brand-border flex items-center justify-center text-base"
                    style={{ backgroundColor: cell?.color || '#f5efe4' }}
                >
                    {cell?.emoji || ''}
                </div>
            );
        }
    }
    return (
        <div className="grid gap-0.5 rounded-2xl overflow-hidden border border-brand-border" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
            {grid}
        </div>
    );
}

const FIND_WARNING = {
    wild: '⚠️ Wild plants can have toxic look-alikes. Never eat anything identified solely from a photo — always cross-check with a local expert.',
    mushroom: '☠️ Mushrooms can be deadly poisonous. Never eat a wild mushroom based on a photo alone — always have it verified by a qualified mycologist.',
};

function FindCard({ find, onLike }) {
    const { t, locale } = useI18n();
    const [liking, setLiking] = useState(false);
    const [translated, setTranslated] = useState(null);
    const [showOrig, setShowOrig] = useState(false);
    const [translating, setTranslating] = useState(false);
    const like = async () => {
        setLiking(true);
        try {
            const r = await api.post(`/finds/${find.id}/like`);
            onLike(find.id, r.data);
        } catch { /* global signup CTA handles 401/403 */ }
        finally { setLiking(false); }
    };
    const translate = async () => {
        if (translated) { setShowOrig((s) => !s); return; }
        setTranslating(true);
        try {
            const r = await api.post(`/finds/${find.id}/translate`, {}, { params: { locale } });
            setTranslated(r.data?.text || find.note);
            setShowOrig(false);
        } catch { toast.error(t('common.error')); }
        finally { setTranslating(false); }
    };
    const report = async () => {
        const reason = window.prompt('Why are you reporting this find? (wrong ID, unsafe advice, spam, …)');
        if (!reason) return;
        try {
            const r = await api.post(`/finds/${find.id}/report`, { reason: reason.slice(0, 120) });
            toast.success(r.data?.hidden ? 'Reported — this find was auto-hidden pending review.' : 'Reported. Thanks — our moderators will review.');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not report');
        }
    };
    const shownNote = translated && !showOrig ? translated : find.note;
    return (
        <div className="bg-brand-card border border-brand-border rounded-3xl overflow-hidden" data-testid={`find-${find.id}`}>
            {findPhotoUrl(find) && (
                <div className="relative">
                    <FindPhoto find={find} publicView alt={find.species_name} loading="lazy" className="w-full aspect-[4/3] object-cover" />
                    {find.photo_count > 1 && (
                        <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-brand-ink/70 text-white text-[11px] font-medium px-2 py-1 rounded-full" data-testid={`find-angles-${find.id}`}>
                            📷 {find.photo_count} angles
                        </span>
                    )}
                </div>
            )}
            <div className="p-4">
                <h3 className="font-serif text-lg text-brand-ink">{find.species_name}</h3>
                {shownNote && <p className="text-sm text-brand-ink3 mt-1 line-clamp-3">{shownNote}</p>}
                {find.note && (
                    <button data-testid={`find-translate-${find.id}`} onClick={translate} disabled={translating}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-green hover:underline disabled:opacity-50">
                        {translating ? <Loader2 size={11} className="animate-spin" /> : <Languages size={11} />}
                        {translated ? (showOrig ? t('community.translate') : t('community.show_original')) : t('community.translate')}
                    </button>
                )}
                <div className="flex items-center gap-2 mt-2 text-xs text-brand-ink3">
                    <span className="inline-flex items-center gap-1"><User size={11} /> {find.author || 'A forager'}</span>
                    {find.created_at && <span>· {fmtWhen(find.created_at)}</span>}
                </div>
                <div className="mt-3 flex items-center gap-3">
                    <button
                        onClick={like}
                        disabled={liking}
                        data-testid={`btn-like-find-${find.id}`}
                        className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1.5 text-sm transition-colors ${find.liked ? 'bg-brand-green/15 border-brand-green/40 text-brand-green' : 'bg-brand-card border-brand-border hover:bg-brand-bg2'}`}
                    >
                        <Heart size={13} fill={find.liked ? 'currentColor' : 'none'} /> {find.like_count || 0}
                    </button>
                    <button
                        onClick={report}
                        data-testid={`btn-report-find-${find.id}`}
                        className="ml-auto inline-flex items-center gap-1 text-xs text-brand-ink3 hover:text-brand-terracotta"
                    >
                        <Flag size={12} /> Report
                    </button>
                </div>
            </div>
        </div>
    );
}

export function CommunityFeed() {
    const { t } = useI18n();
    const [tab, setTab] = useState('beds'); // beds | wild | mushroom
    const [beds, setBeds] = useState([]);
    const [finds, setFinds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const [sort, setSort] = useState('recent');
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        setLoadError(false);
        (async () => {
            try {
                if (tab === 'beds') {
                    const r = await api.get('/community/beds', { params: { sort } });
                    setBeds(r.data || []);
                } else {
                    const r = await api.get('/finds/community', { params: { kind: tab } });
                    setFinds(r.data?.items || []);
                }
            } catch {
                setBeds([]); setFinds([]);
                setLoadError(true);
            } finally { setLoading(false); }
        })();
    }, [tab, sort, retryKey]);

    const onLikeFind = (id, data) => {
        setFinds((list) => list.map((f) => (f.id === id ? { ...f, liked: data.liked, like_count: data.like_count } : f)));
    };

    const goBack = () => navigate('/dashboard');

    const TABS = [
        ['beds', 'Beds', Sprout],
        ['wild', 'Wild finds', TreePine],
        ['mushroom', 'Mushrooms', null],
    ];

    return (
        <div className="px-5 pt-10 pb-32 max-w-3xl mx-auto" data-testid="community-feed">
            <button onClick={goBack} data-testid="btn-community-back" className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back to app</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">GrowDaily Community</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-4">Shared by gardeners</h1>
            <button onClick={() => navigate('/community/eden')} className="w-full text-left rounded-2xl border border-brand-border bg-brand-card p-4 mb-5 min-h-11">
                <span className="font-semibold">{t('eden.showcase_title')} →</span>
                <span className="block text-sm text-brand-ink3 mt-1">{t('eden.showcase_public_note')}</span>
            </button>

            <div className="flex gap-1.5 mb-5 bg-brand-card border border-brand-border rounded-full p-1 w-full sm:w-fit overflow-x-auto" data-testid="community-tabs">
                {TABS.map(([val, lbl, Icon]) => (
                    <button
                        key={val}
                        data-testid={`community-tab-${val}`}
                        onClick={() => setTab(val)}
                        className={`flex-1 sm:flex-none whitespace-nowrap inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-sm transition-colors ${tab === val ? 'bg-brand-green text-white' : 'text-brand-ink2 hover:text-brand-ink'}`}
                    >
                        {Icon ? <Icon size={13} /> : <span>🍄</span>} {lbl}
                    </button>
                ))}
            </div>

            {tab === 'beds' && (
                <div className="flex gap-1.5 mb-6 bg-brand-card border border-brand-border rounded-full p-1 w-fit" data-testid="community-sort">
                    {[['recent', 'Newest'], ['popular', 'Popular']].map(([val, lbl]) => (
                        <button
                            key={val}
                            data-testid={`sort-${val}`}
                            onClick={() => setSort(val)}
                            className={`px-4 py-1.5 rounded-full text-sm transition-colors ${sort === val ? 'bg-brand-green text-white' : 'text-brand-ink2 hover:text-brand-ink'}`}
                        >
                            {lbl}
                        </button>
                    ))}
                </div>
            )}

            {(tab === 'wild' || tab === 'mushroom') && (
                <div className="mb-6 flex items-start gap-2 text-xs text-brand-terracotta bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-2xl p-3" data-testid="community-safety-warning">
                    <AlertTriangle size={14} strokeWidth={1.8} className="flex-shrink-0 mt-0.5" />
                    <span>{FIND_WARNING[tab]}</span>
                </div>
            )}

            {loading ? (
                <div className="text-center py-16"><Loader2 className="animate-spin inline" size={24} /></div>
            ) : loadError ? (
                <div className="text-center py-12 bg-brand-card border border-brand-border rounded-3xl">
                    <p className="text-sm text-brand-ink2 mb-3">The community could not load on this connection.</p>
                    <button onClick={() => setRetryKey((n) => n + 1)} className="bg-brand-green text-white px-5 py-2.5 rounded-full text-sm font-medium">Try again</button>
                </div>
            ) : tab === 'beds' ? (
                beds.length === 0 ? (
                    <p className="text-brand-ink3 text-center py-16" data-testid="empty-community">No shared layouts yet. Be the first to share!</p>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-4 stagger">
                        {beds.map(b => (
                            <button
                                key={b.share_token}
                                onClick={() => navigate(`/community/bed/${b.share_token}`)}
                                data-testid={`community-bed-${b.share_token}`}
                                className="bg-brand-card border border-brand-border rounded-3xl p-4 text-left hover:bg-brand-bg2 transition-colors"
                            >
                                <BedPreview rows={b.rows} cols={b.cols} cells={b.cells} />
                                <div className="mt-3 flex items-baseline justify-between">
                                    <h3 className="font-serif text-lg text-brand-ink">{b.name}</h3>
                                    <div className="flex items-center gap-3 text-xs text-brand-ink3">
                                        <span className="inline-flex items-center gap-1"><Heart size={12} /> {b.like_count || 0}</span>
                                        <span className="inline-flex items-center gap-1"><Eye size={12} /> {b.view_count || 0}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-xs text-brand-ink3">
                                    <span className="inline-flex items-center gap-1"><span className="text-sm leading-none">{flagFor(b.owner_locale)}</span> <User size={11} /> {b.owner_name || 'A gardener'}</span>
                                    {b.shared_at && <span>· {fmtWhen(b.shared_at)}</span>}
                                    {Array.isArray(b.images) && b.images.length > 0 && <span>· 📷 {b.images.length}</span>}
                                </div>
                                {b.share_description && <p className="text-sm text-brand-ink3 mt-1 line-clamp-2">{b.share_description}</p>}
                            </button>
                        ))}
                    </div>
                )
            ) : (
                finds.length === 0 ? (
                    <p className="text-brand-ink3 text-center py-16" data-testid="empty-finds">
                        {tab === 'mushroom' ? 'No mushroom finds shared yet.' : 'No wild finds shared yet.'} Log one from the Wild guide and share it!
                    </p>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-4 stagger">
                        {finds.map((f) => <FindCard key={f.id} find={f} onLike={onLikeFind} />)}
                    </div>
                )
            )}
        </div>
    );
}

export function CommunityBedDetail() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { locale } = useI18n();
    const [bed, setBed] = useState(null);
    const [loading, setLoading] = useState(true);
    const [liking, setLiking] = useState(false);
    const [translated, setTranslated] = useState(null);
    const [showOriginal, setShowOriginal] = useState(false);
    const [translating, setTranslating] = useState(false);

    const srcLoc = (bed?.owner_locale || 'en').slice(0, 2);
    const tgtLoc = (locale || 'en').slice(0, 2);

    const doTranslate = async () => {
        setTranslating(true);
        try {
            const r = await api.post(`/community/bed/${token}/translate`, {}, { params: { locale: tgtLoc } });
            if (r.data?.text) { setTranslated(r.data.text); setShowOriginal(false); }
            else toast.info('Nothing to translate');
        } catch { toast.error('Translation failed'); }
        finally { setTranslating(false); }
    };

    // Auto-translate the owner's note into the viewer's language when languages differ.
    useEffect(() => {
        if (!bed?.share_description) return;
        const src = (bed.owner_locale || 'en').slice(0, 2);
        const tgt = (locale || 'en').slice(0, 2);
        if (src === tgt) return;
        let cancelled = false;
        api.get(`/community/bed/${token}/translate`, { params: { locale: tgt } })
            .then((r) => { if (!cancelled && r.data?.text) setTranslated(r.data.text); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [bed, locale, token]);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/community/bed/${token}`);
                setBed(r.data);
                // Update OG meta tags for social sharing crawlers
                const ogImg = `${window.location.origin}/api/og/community/bed/${token}.png`;
                const setMeta = (sel, attr, val) => {
                    let el = document.head.querySelector(sel);
                    if (!el) {
                        el = document.createElement('meta');
                        const [, k, v] = sel.match(/\[(\w+)="(.+)"\]/) || [];
                        if (k && v) el.setAttribute(k, v);
                        document.head.appendChild(el);
                    }
                    el.setAttribute(attr, val);
                };
                document.title = `${r.data.name} — GrowDaily community`;
                setMeta('meta[property="og:title"]', 'content', r.data.name);
                setMeta('meta[property="og:description"]', 'content', r.data.share_description || 'Shared bed layout on GrowDaily');
                setMeta('meta[property="og:image"]', 'content', ogImg);
                setMeta('meta[property="og:url"]', 'content', window.location.href);
                setMeta('meta[name="twitter:image"]', 'content', ogImg);
            } catch {
                toast.error('Bed not found');
            } finally { setLoading(false); }
        })();
    }, [token]);

    const like = async () => {
        setLiking(true);
        try {
            const r = await api.post(`/community/bed/${token}/like`);
            setBed(b => ({ ...b, like_count: r.data.like_count }));
        } catch {
            toast.error('Sign in to like');
        } finally { setLiking(false); }
    };

    const flag = async () => {
        const reason = window.prompt('Why are you reporting this bed? (spam, inappropriate, copied, …)');
        if (!reason) return;
        try {
            const r = await api.post(`/community/bed/${token}/flag`, { reason: reason.slice(0, 120) });
            if (r.data?.auto_hidden) {
                toast.success('Reported. This bed has been auto-hidden pending review.');
                navigate('/community');
            } else if (r.data?.already_flagged) {
                toast.info('You already reported this.');
            } else {
                toast.success('Reported. Thanks — our moderators will review.');
            }
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'Could not report');
        }
    };

    const goBack = () => navigate('/community');

    if (loading) return <div className="text-center py-24"><Loader2 className="animate-spin inline" /></div>;
    if (!bed) return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto text-center" data-testid="community-bed-detail">
            <button onClick={goBack} data-testid="btn-community-detail-back" className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back to feed</button>
            <p className="text-brand-ink3 py-12">Not found</p>
        </div>
    );

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="community-bed-detail">
            <button onClick={goBack} data-testid="btn-community-detail-back" className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back to feed</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Bed Design Community</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-2">{bed.name}</h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-brand-ink3 mb-4">
                <span className="inline-flex items-center gap-1"><span className="text-base leading-none">{flagFor(bed.owner_locale)}</span> <User size={13} /> {bed.owner_name || 'A gardener'}</span>
                {bed.planted_at && <span className="inline-flex items-center gap-1"><Sprout size={13} /> Planted {fmtWhen(bed.planted_at)}</span>}
                {bed.shared_at && <span className="inline-flex items-center gap-1"><CalendarDays size={13} /> Shared {fmtWhen(bed.shared_at)}</span>}
            </div>
            {bed.share_description && (
                <div className="mb-6" data-testid="bed-note">
                    <p className="text-brand-ink2">{(translated && !showOriginal) ? translated : bed.share_description}</p>
                    {srcLoc !== tgtLoc && (
                        translated ? (
                            <button
                                onClick={() => setShowOriginal((o) => !o)}
                                data-testid="btn-toggle-translation"
                                className="mt-1.5 inline-flex items-center gap-1 text-xs text-brand-green hover:text-brand-greenDark"
                            >
                                <Languages size={12} /> {showOriginal ? 'Show translation' : `Show original ${flagFor(bed.owner_locale)}`}
                            </button>
                        ) : (
                            <button
                                onClick={doTranslate}
                                disabled={translating}
                                data-testid="btn-translate-note"
                                className="mt-1.5 inline-flex items-center gap-1 text-xs text-brand-green hover:text-brand-greenDark disabled:opacity-50"
                            >
                                {translating ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />} Translate
                            </button>
                        )
                    )}
                </div>
            )}
            <BedPreview rows={bed.rows} cols={bed.cols} cells={bed.cells} />

            {plantLegend(bed.cells).length > 0 && (
                <div className="mt-5" data-testid="bed-legend">
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-2">What's planted</p>
                    <div className="flex flex-wrap gap-2">
                        {plantLegend(bed.cells).map((p, i) => (
                            <span key={p.label || `${p.emoji}-${i}`} className="inline-flex items-center gap-1.5 bg-brand-card border border-brand-border rounded-full pl-2 pr-3 py-1 text-sm text-brand-ink">
                                <span className="text-base leading-none">{p.emoji}</span>
                                <span>{p.label}</span>
                                {p.count > 1 && <span className="text-brand-ink3 text-xs">×{p.count}</span>}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {Array.isArray(bed.images) && bed.images.length > 0 && (
                <div className="mt-5">
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-2">Photos</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="bed-photos">
                        {bed.images.map((src, i) => (
                            <LivingImage key={src} src={publicFileUrl(src)} alt={`${bed.name} photo ${i + 1}`} className="w-full aspect-square rounded-2xl border border-brand-border" />
                        ))}
                    </div>
                    <p className="text-[11px] text-brand-ink3 mt-2 inline-flex items-center gap-1"><Flag size={11} /> See something inappropriate? Use Report below.</p>
                </div>
            )}
            <div className="mt-6 flex items-center gap-3">
                <button
                    onClick={like}
                    disabled={liking}
                    data-testid="btn-like-bed"
                    className="inline-flex items-center gap-2 bg-brand-card border border-brand-border hover:bg-brand-bg2 rounded-full px-4 py-2 text-sm"
                >
                    <Heart size={14} /> {bed.like_count || 0}
                </button>
                <span className="text-xs text-brand-ink3 inline-flex items-center gap-1"><Eye size={12} /> {bed.view_count || 0}</span>
                <ShareButton
                    title={`${bed.name} · GrowDaily`}
                    text={`Check out "${bed.name}" — a garden bed shared on GrowDaily 🌱`}
                    url={typeof window !== 'undefined' ? window.location.href : 'https://growdaily.fun'}
                    label="Share"
                    className="text-brand-green hover:text-brand-greenDark"
                />
                <button
                    onClick={flag}
                    data-testid="btn-flag-bed"
                    className="ml-auto inline-flex items-center gap-1 text-xs text-brand-ink3 hover:text-brand-terracotta"
                    title="Report this bed"
                >
                    <Flag size={12} /> Report
                </button>
            </div>
        </div>
    );
}
